import { useAuth0 } from "@auth0/auth0-react";
import { Tab } from "@headlessui/react";
import { useQueueFuelPlan, useStoreFuelPlan } from "./api";
import { useSendMessage } from "./messaging";
import cn from "classnames";
import Button from "components/button";
import { getCustomerConfig } from "../../../dashboard-config";
import { useState } from "react";
import { BiExit } from "react-icons/bi";
import { FaGasPump } from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";
import { tractorDrivers, whatsAppRecipients, driverIdRecipients } from "./driver-fanout";
import type { NormalizedFuelLocation, NormalizedFuelPlanRoute } from "./services";

interface FuelPlanProps {
  // Rendered with a single route (fuel-optimizer always passes the selected
  // route). The array branch below is a legacy multi-tractor send path.
  fuelPlan: NormalizedFuelPlanRoute;
  setFuelPlan: (plan: NormalizedFuelPlanRoute) => void;
  resetFuelPlan: () => void;
}

const FuelPlan = ({ fuelPlan, setFuelPlan, resetFuelPlan }: FuelPlanProps) => {
  const { user } = useAuth0();
  // Local names mirror the old fetcher API so the send flows read the same.
  const { mutateAsync: queueFuelPlan } = useQueueFuelPlan();
  const { mutateAsync: sendMessage } = useSendMessage();
  const { mutateAsync: storeFuelPlan } = useStoreFuelPlan();
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{ type: "success" | "error"; message: string } | undefined>();
  // For multi tractors
  const [successNotification, setSuccessNotification] = useState("");
  const [errorNotification, setErrorNotifcation] = useState("");
  const [disableSendFuelPlan, setDisableSendFuelPlan] = useState(false);
  const [isEditFuelPlan, setIsEditFuelPlan] = useState(false);
  const [includeBriefDirections, setIncludeBriefDirections] = useState(true);

  // Helper function to properly update the fuel plan based on whether we're in Pro mode
  const updateFuelPlan = (updatedPlan: NormalizedFuelPlanRoute) => {
    // If the setFuelPlan function is a wrapper (Pro mode), it expects a direct object
    // If it's the original setState function, we can use either approach
    setFuelPlan(updatedPlan);
  };

  const sendFuelPlan = async () => {
    setLoading(true);

    const customerCode = user!["https://ifuelsmart.com/company"];

    let message = "Fuel Plan: \n";
    fuelPlan.fuelPurchaseLocations.forEach((fpl, idx) => {
      message += `
      
      ${idx + 1}. ${fpl.location}:
      - ${fpl.fuelToPurchase === -99 ? " Top off tractor" : ` Purchase ${fpl.fuelToPurchase} gallons`}
      -  Exit: ${fpl.interstate_exit}
      \n`;
    });

    // Add brief directions if available
    if (includeBriefDirections && fuelPlan?.brief_directions?.length) {
      message += `\nDirections: ${fuelPlan.brief_directions.join(", ")}\n`;
    }

    console.log(fuelPlan);
    // If there is no driver assigned to the tractor, we need to queue the fuel plan
    if (fuelPlan.tractor.driver_id === null || !fuelPlan.tractor.driver_id) {
      try {
        await queueFuelPlan({
          customer: user!["https://ifuelsmart.com/company"],
          unitNumber: fuelPlan.tractor.unit_number,
          fuelPlan: {
            origin: fuelPlan.origin,
            destination: fuelPlan.destination,
            directionsVia: fuelPlan.directionsVia,
            totalTripDistanceMiles: fuelPlan.totalTripDistanceMiles,
            totalFuelNeededGallons: fuelPlan.totalFuelNeededGallons,
            totalPurchaseNeededGallons: fuelPlan.totalPurchaseNeededGallons,
            fuelPurchaseLocations: fuelPlan.fuelPurchaseLocations,
            fuelStationOnRoute: fuelPlan.fuelStationOnRoute,
            tractor: fuelPlan.tractor, // queue fuel plan processor relies on this,
            savings: fuelPlan?.ifsOptimizedSavings ?? null,
          },
        });

        setNotification({ type: "success", message: "Fuel plan queued" });
        setDisableSendFuelPlan(true);
        setLoading(false);
      } catch (error) {
        setNotification({
          type: "error",
          message: (error as { response: { data: { message: string } } }).response.data.message,
        });
        setLoading(false);
      }

      return;
    }

    // If there is a driver assigned to the tractor, we send the fuel plan to the driver
    try {
      // Send message with Whatsapp
      if (getCustomerConfig(customerCode).sendsFuelPlanViaWhatsApp) {
        // Fan out to every team driver that has a phone number (deduped).
        const recipients = whatsAppRecipients(tractorDrivers(fuelPlan.tractor));
        if (recipients.length === 0) {
          setNotification({ type: "error", message: "No Phone #" });
          setDisableSendFuelPlan(true);
          setLoading(false);
          return;
        }

        await Promise.all(
          recipients.map((driver) =>
            sendMessage({
              customer: customerCode,
              driver,
              fuelPurchaseLocations: fuelPlan.fuelPurchaseLocations,
              ...(includeBriefDirections && fuelPlan.brief_directions?.length
                ? { brief_directions: fuelPlan.brief_directions }
                : {}),
            }),
          ),
        );

        await storeFuelPlan({
          customer: user!["https://ifuelsmart.com/company"],
          unitNumber: fuelPlan.tractor.unit_number,
          fuelPlan: {
            origin: fuelPlan.origin,
            destination: fuelPlan.destination,
            directionsVia: fuelPlan.directionsVia,
            totalTripDistanceMiles: fuelPlan.totalTripDistanceMiles,
            totalFuelNeededGallons: fuelPlan.totalFuelNeededGallons,
            totalPurchaseNeededGallons: fuelPlan.totalPurchaseNeededGallons,
            fuelPurchaseLocations: fuelPlan.fuelPurchaseLocations,
            fuelStationOnRoute: fuelPlan.fuelStationOnRoute,
            savings: fuelPlan?.ifsOptimizedSavings ?? null, // Not entirely sure if this is present since we have different algos
          },
        });

        setNotification({ type: "success", message: "Fuel plan sent" });
        setDisableSendFuelPlan(true);
        setLoading(false);

        return;
      }

      // solid old implementatino — fan out to every team driver (deduped).
      await Promise.all(
        driverIdRecipients(tractorDrivers(fuelPlan.tractor)).map((driverId) =>
          sendMessage({
            to: driverId,
            body: message.replace(/[^\S\r\n]+/g, " "),
            customer: user!["https://ifuelsmart.com/company"],
          }),
        ),
      );

      await storeFuelPlan({
        customer: user!["https://ifuelsmart.com/company"],
        unitNumber: fuelPlan.tractor.unit_number,
        fuelPlan: {
          origin: fuelPlan.origin,
          destination: fuelPlan.destination,
          directionsVia: fuelPlan.directionsVia,
          totalTripDistanceMiles: fuelPlan.totalTripDistanceMiles,
          totalFuelNeededGallons: fuelPlan.totalFuelNeededGallons,
          totalPurchaseNeededGallons: fuelPlan.totalPurchaseNeededGallons,
          fuelPurchaseLocations: fuelPlan.fuelPurchaseLocations,
          fuelStationOnRoute: fuelPlan.fuelStationOnRoute,
          savings: fuelPlan?.ifsOptimizedSavings ?? null, // Not entirely sure if this is present since we have different algos
        },
      });

      setNotification({ type: "success", message: "Fuel plan sent" });
      setDisableSendFuelPlan(true);
      setLoading(false);
    } catch (error) {
      setNotification({
        type: "error",
        message: (error as { response: { data: { message: string } } }).response.data.message,
      });
      setLoading(false);
    }
  };

  // Send multi-tractor fuel plans
  const sendFuelPlans = async () => {
    setErrorNotifcation("");
    setSuccessNotification("");
    setLoading(true);

    // Legacy multi-tractor path: here the plan is the full array of routes.
    const plans = fuelPlan as unknown as NormalizedFuelPlanRoute[];
    const sendFuelPlanResponses = await Promise.allSettled(
      plans.map(async (singleFuelPlan, i) => {
        // Do this for each fuel plan
        let message = "Fuel Plan: \n";
        singleFuelPlan.fuelPurchaseLocations.forEach((fpl, idx) => {
          message += `
            ${idx + 1}. ${fpl.location}:
            - ${fpl.fuelToPurchase === -99 ? " Top off tractor" : ` Purchase ${fpl.fuelToPurchase} gallons`}
            -  Exit: ${fpl.interstate_exit}
            \n`;
        });

        // Fan out to every team driver on this tractor (deduped).
        await Promise.all(
          driverIdRecipients(tractorDrivers(singleFuelPlan.tractor)).map((driverId) =>
            sendMessage({
              to: driverId,
              body: message.replace(/[^\S\r\n]+/g, " "),
              customer: user!["https://ifuelsmart.com/company"],
            }),
          ),
        );

        storeFuelPlan({
          customer: user!["https://ifuelsmart.com/company"],
          unitNumber: singleFuelPlan.tractor.unit_number,
          fuelPlan: {
            origin: singleFuelPlan.origin,
            destination: singleFuelPlan.destination,
            directionsVia: singleFuelPlan.directionsVia,
            totalTripDistanceMiles: singleFuelPlan.totalTripDistanceMiles,
            totalFuelNeededGallons: singleFuelPlan.totalFuelNeededGallons,
            totalPurchaseNeededGallons: singleFuelPlan.totalPurchaseNeededGallons,
            fuelPurchaseLocations: singleFuelPlan.fuelPurchaseLocations,
            fuelStationOnRoute: singleFuelPlan.fuelStationOnRoute,
            savings: singleFuelPlan?.ifsOptimizedSavings ?? null, // Not entirely sure if this is present since we have different algos
          },
        });
      }),
    );

    let failedToSendTractors: string[] = [];
    let successfullySentTractors: string[] = [];

    sendFuelPlanResponses.forEach((fuelPlansResponse, idx) => {
      if (fuelPlansResponse.status === "rejected") {
        failedToSendTractors.push(plans[idx].tractor.unit_number);
      } else if (fuelPlansResponse.status === "fulfilled") {
        successfullySentTractors.push(plans[idx].tractor.unit_number);
      }
    });

    if (failedToSendTractors.length) {
      setErrorNotifcation(`Failed to send fuel plan to ${failedToSendTractors.join(", ")}`);
    }
    if (successfullySentTractors.length) {
      setSuccessNotification(`Successfully sent fuel plan to ${successfullySentTractors.join(", ")}`);
    }

    setDisableSendFuelPlan(true);
    setLoading(false);
  };

  // Due to the fact that we implement multi-tractor fuel plans, we need to check if the fuel plan is an array
  // If it is, we need to show a message to the user that viewing fuel plan is only available in single tractor mode
  if (Array.isArray(fuelPlan)) {
    return (
      <div className="flex flex-col h-full p-3">
        <div className="bg-white rounded-xl shadow-md flex flex-col h-full overflow-hidden">
          <div className="mx-3 mt-3">
            <div className="bg-orange-50 border-l-4 border-orange-500 text-orange-700 p-3 rounded-r-lg text-sm">
              Viewing fuel plan is only available in single tractor mode
            </div>
            {/* Alert */}
            {errorNotification && (
              <div
                className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mt-3 flex items-center text-sm"
                role="alert"
              >
                {errorNotification}
              </div>
            )}
            {successNotification && (
              <div
                className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg mt-3 flex items-center text-sm"
                role="alert"
              >
                {successNotification}
              </div>
            )}
          </div>
          <div className="border-t flex-0 bg-gray-50 mt-auto" style={{ flex: "0 0 auto" }}>
            {/* Actions */}
            <div className="flex items-center justify-between px-4 py-3">
              <Button
                variant="lightGray"
                onClick={resetFuelPlan}
                className="px-3 py-1.5 rounded-md shadow-sm hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                Back
              </Button>
              <Button
                className="px-4 py-1.5 rounded-md shadow-sm bg-orange-600 hover:bg-orange-700 text-sm font-medium"
                disabled={disableSendFuelPlan}
                loading={loading}
                onClick={sendFuelPlans}
              >
                Send Fuel Plans
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hanldeActivateFuelStop = (idx: number) => {
    const newFuelPurchaseLocations = fuelPlan.fuelPurchaseLocations.map((fpl, i) => {
      if (i === idx) {
        return {
          ...fpl,
          active: true,
        };
      } else {
        return fpl;
      }
    });

    // Using the helper function to update the fuel plan
    updateFuelPlan({
      ...fuelPlan,
      fuelPurchaseLocations: newFuelPurchaseLocations,
    });
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Tab.Group as="div" className="flex flex-col h-full bg-white overflow-hidden">
        <Tab.List className="flex flex-initial flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
          <Tab
            className={({ selected }) =>
              cn(
                "flex-1 py-2 text-xs font-medium focus:outline-none transition-colors",
                selected
                  ? "text-orange-600 border-b-2 border-orange-500 bg-orange-50"
                  : "text-gray-600 hover:text-orange-500 hover:bg-orange-50/50 border-b-2 border-transparent",
              )
            }
          >
            IFS Recommended
          </Tab>
          <Tab
            className={({ selected }) =>
              cn(
                "flex-1 py-2 text-xs font-medium focus:outline-none transition-colors",
                selected
                  ? "text-orange-600 border-b-2 border-orange-500 bg-orange-50"
                  : "text-gray-600 hover:text-orange-500 hover:bg-orange-50/50 border-b-2 border-transparent",
              )
            }
          >
            All Fuel Stations
          </Tab>
        </Tab.List>
        <Tab.Panels className="flex-1 overflow-hidden">
          <Tab.Panel className="h-full flex flex-col overflow-hidden">
            {/* Actions to edit fuel solution */}
            <div className="px-3 pt-2 pb-2 border-b space-x-2 bg-gray-50 flex">
              {isEditFuelPlan ? (
                <>
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs font-medium text-white shadow-sm bg-green-600 hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 transition-colors flex items-center"
                    onClick={() => setIsEditFuelPlan(false)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Finish
                  </button>
                  <button
                    type="button"
                    className="rounded px-2 py-1 text-xs font-medium text-white shadow-sm bg-gray-700 hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 transition-colors flex items-center"
                    onClick={() => {
                      // The added stop omits `include` (present on real fuel stops);
                      // cast to keep the existing shape unchanged.
                      const newFuelPurchaseLocations = fuelPlan.fuelPurchaseLocations.concat({
                        location: "",
                        price: 0,
                        fuelToPurchase: 0,
                        lat: 0,
                        lng: 0,
                        interstate_exit: "",
                        active: false,
                        loc_id: uuidv4(),
                      } as unknown as NormalizedFuelLocation);

                      updateFuelPlan({
                        ...fuelPlan,
                        fuelPurchaseLocations: newFuelPurchaseLocations,
                      });
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Add Stop
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="rounded px-2 py-1 text-xs font-medium text-white shadow-sm bg-orange-600 hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 transition-colors flex items-center"
                  onClick={() => setIsEditFuelPlan(true)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 mr-1"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  Edit Plan
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {isEditFuelPlan ? (
                <div className="p-2 space-y-2">
                  {fuelPlan.fuelPurchaseLocations.map((fpl, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-x-2 bg-white p-2 rounded border border-gray-200">
                      {/* Fuel Station  */}
                      <div className="col-span-4">
                        <label htmlFor="location" className="block text-xs font-medium leading-4 text-gray-700 mb-1">
                          <FaGasPump className="text-orange-600 mr-1 text-xs inline" />
                          Fuel Location
                        </label>
                        <div className="relative rounded shadow-sm">
                          <select
                            className="block w-full rounded border-0 py-1 pl-2 pr-7 text-sm ring-1 ring-inset ring-gray-300 focus:ring-1 focus:ring-orange-600 appearance-none bg-gray-50"
                            value={fpl.location}
                            onChange={(e) => {
                              const selectedFuelLocation = e.target.value;

                              if (selectedFuelLocation === "") {
                                // If user select empty location, we need to set the values to empty
                                const newFuelPurchaseLocations = fuelPlan.fuelPurchaseLocations.map((fpl, i) => {
                                  if (i === idx) {
                                    return {
                                      ...fpl,
                                      location: "",
                                      price: 0,
                                      fuelToPurchase: 0,
                                      lat: 0,
                                      lng: 0,
                                      interstate_exit: "",
                                      active: false,
                                    };
                                  } else {
                                    return fpl;
                                  }
                                });

                                // Direct object update instead of callback
                                updateFuelPlan({
                                  ...fuelPlan,
                                  fuelPurchaseLocations: newFuelPurchaseLocations,
                                });

                                return;
                              }

                              const fuelLocation = fuelPlan.fuelStationOnRoute.data.find(
                                (fsor) => fsor[0] === selectedFuelLocation,
                              )!;
                              const fuelLocationPrice = fuelLocation[1];
                              const fuelLocationLat = fuelLocation[2];
                              const fuelLocationLon = fuelLocation[3];
                              const fuelLocationInterstate = fuelLocation[4];

                              const newFuelPurchaseLocations = fuelPlan.fuelPurchaseLocations.map((fpl, i) => {
                                if (i === idx) {
                                  return {
                                    ...fpl,
                                    location: selectedFuelLocation,
                                    price: fuelLocationPrice,
                                    lat: fuelLocationLat,
                                    lng: fuelLocationLon,
                                    interstate_exit: fuelLocationInterstate,
                                  };
                                } else {
                                  return fpl;
                                }
                              });

                              // Direct object update instead of callback
                              updateFuelPlan({
                                ...fuelPlan,
                                fuelPurchaseLocations: newFuelPurchaseLocations,
                              });
                            }}
                          >
                            <option value="">Select location</option>
                            {fuelPlan.fuelStationOnRoute.data.map(([location], idx) => (
                              <option key={idx} value={location}>
                                {location}
                              </option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center px-1 pointer-events-none">
                            <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                      {/* Fuel Amount */}
                      <div className="col-span-1">
                        <label htmlFor="gallons" className="block text-xs font-medium leading-4 text-gray-700 mb-1">
                          Gallons
                        </label>
                        <div className="relative rounded shadow-sm">
                          <input
                            id="gallons"
                            className="block w-full rounded border-0 py-1 pl-2 pr-2 text-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-orange-600 bg-gray-50"
                            placeholder="Amt"
                            value={fpl.fuelToPurchase === -99 ? fuelPlan.tractor.tank_capacity : fpl.fuelToPurchase}
                            onChange={(e) => {
                              const fuelToPurchase = e.target.value;

                              const newFuelPurchaseLocations = fuelPlan.fuelPurchaseLocations.map((fpl, i) => {
                                if (i === idx) {
                                  return {
                                    ...fpl,
                                    fuelToPurchase: fuelToPurchase === "" ? 0 : parseInt(fuelToPurchase),
                                  };
                                } else {
                                  return fpl;
                                }
                              });

                              // Direct object update instead of callback
                              updateFuelPlan({
                                ...fuelPlan,
                                fuelPurchaseLocations: newFuelPurchaseLocations,
                              });
                            }}
                          />
                        </div>
                      </div>
                      {/* Edit Fuel Stop Actions */}
                      <div className="col-span-1 self-end flex space-x-1">
                        {!fpl.active && (
                          <button
                            type="button"
                            className="rounded flex-1 bg-green-600 px-0 py-1 text-xs font-medium text-white shadow-sm hover:bg-green-500 focus-visible:outline-green-600 transition-colors"
                            onClick={() => hanldeActivateFuelStop(idx)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3 w-3 mx-auto"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        )}

                        <button
                          type="button"
                          className="rounded flex-1 bg-red-600 px-0 py-1 text-xs font-medium text-white shadow-sm hover:bg-red-500 focus-visible:outline-red-600 transition-colors"
                          onClick={() => {
                            const newFuelPurchaseLocations = fuelPlan.fuelPurchaseLocations.filter((fpl, i) => {
                              return i !== idx;
                            });

                            // Direct object update instead of callback
                            updateFuelPlan({
                              ...fuelPlan,
                              fuelPurchaseLocations: newFuelPurchaseLocations,
                            });
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 mx-auto"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-1 divide-y">
                  {fuelPlan.fuelPurchaseLocations.map((fpl, idx) => (
                    <div key={idx} className="px-2 py-2 hover:bg-gray-50 transition-colors">
                      <div className="flex gap-2">
                        <div className="flex-shrink-0 self-start pt-0.5">
                          <span className="flex items-center justify-center bg-orange-500 text-white rounded-full w-6 h-6 text-xs font-medium">
                            {idx + 1}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 mb-1">{fpl.location}</div>

                          <div className="flex items-start mb-1">
                            <FaGasPump className="text-orange-600 text-xs mt-0.5 mr-1.5 flex-shrink-0" />
                            <div className="text-xs leading-tight text-gray-700">
                              {fpl.fuelToPurchase === -99
                                ? "Top off tractor at "
                                : `Purchase ${fpl.fuelToPurchase} gal at `}
                              <span className="inline-block bg-green-100 border border-green-200 px-1 py-0.5 rounded text-green-800 text-xs font-medium whitespace-nowrap ml-0.5">
                                ${fpl.price.toFixed(2)}/gal
                              </span>
                            </div>
                          </div>

                          <div className="flex items-start">
                            <BiExit className="text-gray-500 text-xs mt-0.5 mr-1.5 flex-shrink-0" />
                            <div className="text-xs leading-tight text-gray-600">Exit: {fpl.interstate_exit}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!isEditFuelPlan && (
              // Summary
              <div className="border-t bg-gray-50">
                {/* Brief Directions (Pro Version) - Scrollable */}
                {fuelPlan.brief_directions && fuelPlan.brief_directions.length > 0 && (
                  <div className="border-b border-gray-200 pl-3 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3.5 w-3.5 mr-1 text-blue-500"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M12 1.586l-4 4V17h8V5.586l-4-4zM2 17V9h3v8H2zm5 0V9h2v8H7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-xs font-medium text-gray-600">Brief Directions:</span>
                      </div>
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 accent-blue-600"
                          checked={includeBriefDirections}
                          onChange={(event) => setIncludeBriefDirections(event.target.checked)}
                        />
                        Include in driver message
                      </label>
                    </div>
                    <div className="max-h-[70px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                      <div className="grid grid-cols-1 gap-1">
                        {fuelPlan.brief_directions.map((direction, idx) => (
                          <div key={idx} className="flex items-center text-xs text-gray-700">
                            <span className="flex-shrink-0 bg-blue-100 text-blue-800 font-medium rounded-full w-4 h-4 flex items-center justify-center mr-1.5 text-[10px]">
                              {idx + 1}
                            </span>
                            {direction}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary */}
                <div className="border-b border-gray-200">
                  {/* Row */}
                  <div className="grid grid-cols-3 px-3 py-2 gap-2">
                    <div className="text-xs">
                      <div className="text-gray-600 mb-0.5 flex items-center text-xs">
                        <span className="w-1 h-1 bg-gray-400 rounded-full mr-1"></span>
                        Distance
                      </div>
                      <div className="font-medium">{fuelPlan.totalTripDistanceMiles.toLocaleString()} Miles</div>
                    </div>
                    <div className="text-xs">
                      <div className="text-gray-600 mb-0.5 flex items-center text-xs">
                        <span className="w-1 h-1 bg-green-500 rounded-full mr-1"></span>
                        IFS Savings
                      </div>
                      <div className="font-medium text-green-700">${fuelPlan.ifsOptimizedSavings}</div>
                    </div>
                    <div className="text-xs">
                      <div className="text-gray-600 mb-0.5 flex items-center text-xs">
                        <span className="w-1 h-1 bg-orange-500 rounded-full mr-1"></span>
                        Fuel Stops
                      </div>
                      <div className="font-medium">{fuelPlan.fuelPurchaseLocations.length}</div>
                    </div>
                  </div>
                </div>
                {/* Mail Details */}
                <div className="border-b border-gray-200 bg-gray-100">
                  {/* Row */}
                  <div className="grid grid-cols-2 gap-2 px-3 py-2">
                    <div className="text-xs">
                      <div className="text-gray-600 mb-0.5 uppercase tracking-wide text-xs">Tractor</div>
                      <div className="font-medium">{fuelPlan.tractor.unit_number}</div>
                    </div>
                    <div className="text-xs">
                      <div className="text-gray-600 mb-0.5 uppercase tracking-wide text-xs">
                        {tractorDrivers(fuelPlan.tractor).length > 1 ? "Drivers" : "Driver"}
                      </div>
                      <div className="font-medium">
                        {tractorDrivers(fuelPlan.tractor)
                          .map((d) => d.driver_name)
                          .filter(Boolean)
                          .join(", ") || "- Not Found -"}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center justify-between px-3 py-2 space-x-2">
                  <Button
                    variant="lightGray"
                    onClick={resetFuelPlan}
                    className="px-3 py-1.5 rounded shadow-sm hover:bg-gray-200 transition-colors font-medium text-xs"
                  >
                    Back
                  </Button>
                  <Button
                    variant="custom"
                    disabled={disableSendFuelPlan || loading}
                    loading={loading}
                    onClick={sendFuelPlan}
                    className={cn(
                      "px-3 py-1.5 rounded shadow-sm transition-all font-medium text-xs text-white disabled:cursor-not-allowed",
                      {
                        "bg-green-700 hover:bg-green-800 disabled:bg-green-800": notification?.type === "success",
                        "bg-red-700 hover:bg-red-800 disabled:bg-red-800": notification?.type === "error",
                        "bg-orange-600 hover:bg-orange-700": !notification?.type,
                      }
                    )}
                  >
                    {notification?.message ? notification?.message : "Send Fuel Plan"}
                  </Button>
                </div>
              </div>
            )}
          </Tab.Panel>
          <Tab.Panel className="h-full flex flex-col overflow-hidden">
            <div className="py-1 divide-y h-full lg:overflow-y-auto lg:flex-grow lg:h-1">
              {fuelPlan.fuelStationOnRoute.data.map(([location, price, lat, lng, interstate_exit], idx) => (
                <div key={idx} className="px-2 py-2 hover:bg-gray-50 transition-colors">
                  <div className="flex gap-2">
                    <div className="flex-shrink-0 self-start pt-0.5">
                      <span className="flex items-center justify-center bg-orange-500 text-white rounded-full w-6 h-6 text-xs font-medium">
                        {idx + 1}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 mb-1">{location}</div>

                      <div className="flex items-start mb-1">
                        <FaGasPump className="text-orange-600 text-xs mt-0.5 mr-1.5 flex-shrink-0" />
                        <div className="text-xs leading-tight text-gray-700">
                          Fuel price at this location is{" "}
                          <span className="inline-block bg-green-100 border border-green-200 px-1 py-0.5 rounded text-green-800 text-xs font-medium whitespace-nowrap ml-0.5">
                            ${price.toFixed(2)}/gal
                          </span>
                        </div>
                      </div>

                      {interstate_exit && (
                        <div className="flex items-start">
                          <BiExit className="text-gray-500 text-xs mt-0.5 mr-1.5 flex-shrink-0" />
                          <div className="text-xs leading-tight text-gray-600">Exit: {interstate_exit}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

export default FuelPlan;
